import React from "react";
import { Nav, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./LeadTaskResponsive.css";
import {
  faBrain,
  faClipboard,
  faEnvelope,
  faHome,
  faPaperclip,
  faPhone,
  faSquareCheck,
  faTimeline,
  faUser,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import LeadDetailsTimeline from "../../../timelines/LeadDetailsTimeline";
import LeadEmails from "../LeadEmails";
import LeadDetailsCommunication from "./LeadDetailsCall";
import LeadDetailsMeeting from "./LeadDetailsMeeting";
import LeadDetailsNotes from "./LeadDetailsNotes";
import LeadDetailsTask from "./LeadDetailsTask";
import LeadDetailsAttachments from "./LeadDetailsAttachments";
import LeadAiAssistant from "../LeadAiAssistant";
import BuyerProfilePage from "../BuyerProfilePage";
import SellerProfilePage from "../SellerProfilePage";

interface TabItem {
  name: string;
  icon: IconProp;
  content: JSX.Element;
  key: string; // Add key for lazy loading
}

const LeadDetailsTab = ({
  tasks,
  notes,
  isLoading,
  callNotes,
  activities,
  leadDetails,
  meetingNotes,
  attachmentsLv,
  handleSearchTask,
  handleSearchMeeting,
  handleSearchInputChange,
  onWorkflowProgressRefresh,
  onRefreshActivities,
}: any) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  
  // Get current active tab
  const activeTab = type?.toLowerCase() || "activity";

  // Determine lead types
  const leadType = leadDetails?.lead_type?.lead_type || leadDetails?.lead_type || "";
  const isBuyer = leadType.toLowerCase().includes("buyer");
  const isSeller = leadType.toLowerCase().includes("seller");
  const isInvestor = leadType.toLowerCase().includes("investor");

  // Lazy loading function - only render content when tab is active
  const renderTabContent = (tabKey: string, content: JSX.Element) => {
    if (activeTab === tabKey) {
      return content;
    }
    return <div>Loading...</div>; // Placeholder for inactive tabs
  };

  // Base tabs with lazy loading
  const baseTabs: TabItem[] = [
    {
      name: "Activity",
      key: "activity",
      icon: faTimeline,
      content: renderTabContent("activity", (
        <LeadDetailsTimeline
          activities={activities}
          allActivities={activities || []}
          isLoading={isLoading}
          handleSearchInputChange={handleSearchInputChange}
        />
      )),
    },
    {
      name: "Emails",
      key: "emails", 
      icon: faEnvelope,
      content: renderTabContent("emails", <LeadEmails lead={leadDetails} onRefreshActivities={onRefreshActivities} />),
    },
    {
      name: "Call",
      key: "call",
      icon: faPhone,
      content: renderTabContent("call", (
        <LeadDetailsCommunication phoneNotes={callNotes} leadDetails={leadDetails} />
      )),
    },
    {
      name: "Meeting",
      key: "meeting",
      icon: faVideo,
      content: renderTabContent("meeting", (
        <LeadDetailsMeeting
          meetings={meetingNotes}
          handleSearchMeeting={handleSearchMeeting}
        />
      )),
    },
    {
      name: "Notes",
      key: "notes",
      icon: faClipboard,
      content: renderTabContent("notes", <LeadDetailsNotes notes={notes} />),
    },
    {
      name: "Task",
      key: "task",
      icon: faSquareCheck,
      content: renderTabContent("task", (
        <LeadDetailsTask 
          tasks={tasks} 
          handleSearchTask={handleSearchTask} 
          onWorkflowProgressRefresh={onWorkflowProgressRefresh}
        />
      )),
    },
    {
      name: "Attachments",
      key: "attachments",
      icon: faPaperclip,
      content: renderTabContent("attachments", (
        <LeadDetailsAttachments
          attachmentsLv={attachmentsLv}
        />
      )),
    },
    {
      name: "AI Assistant",
      key: "ai assistant",
      icon: faBrain,
      content: renderTabContent("ai assistant", <LeadAiAssistant leadDetails={leadDetails} />),
    },
  ];

  // Conditional profile tabs based on lead type
  const profileTabs: TabItem[] = [];
  
  if (isBuyer || isInvestor) {
    profileTabs.push({
      name: "Buyer Profile",
      key: "buyer profile",
      icon: faUser,
      content: renderTabContent("buyer profile", (
        <BuyerProfilePage leadId={leadDetails?.id} leadDetails={leadDetails} />
      )),
    });
  }
  
  if (isSeller) {
    profileTabs.push({
      name: "Seller Profile",
      key: "seller profile", 
      icon: faHome,
      content: renderTabContent("seller profile", (
        <SellerProfilePage leadId={leadDetails?.id} leadDetails={leadDetails} />
      )),
    });
  }

  // Combine base tabs with conditional profile tabs
  const tabitems: TabItem[] = [...baseTabs, ...profileTabs];

  const handleAddQueryParams = (value: any) => {
    const queryParams = new URLSearchParams({
      type: value,
    });

    navigate(`?${queryParams.toString().toLowerCase()}`);
  };

  return (
    <div className="modern-lead-tabs">
      <Tab.Container activeKey={activeTab}>
        {/* Mobile tab indicator for current tab */}
        <div className="d-block d-md-none mb-2 px-2">
          <h5 className="mobile-tab-indicator fw-semibold">
            <FontAwesomeIcon 
              icon={tabitems.find(item => item.name.toLowerCase() === activeTab)?.icon || faTimeline} 
              className="me-2" 
            />
            {tabitems.find(item => item.name.toLowerCase() === activeTab)?.name || "Activity"}
          </h5>
        </div>
        
        {/* Modern Tab Navigation - made more visible for mobile */}
        <div className="modern-tab-wrapper mobile-tab-nav">
          <div className="mobile-tabs-scroll-container">
            <Nav className="modern-nav-tabs" role="tablist">
              {tabitems.map((item) => (
                <Nav.Item key={item.name} className="modern-nav-item">
                  <Nav.Link 
                    eventKey={item.name.toLowerCase()}
                    onClick={() => handleAddQueryParams(item.name)}
                    className={`modern-nav-link ${activeTab === item.name.toLowerCase() ? 'active' : ''}`}
                  >
                    <div className="tab-icon-wrapper">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="tab-icon"
                      />
                    </div>
                    <span className="tab-label">{item.name}</span>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="modern-tab-content">
          <Tab.Content>
            {tabitems.map((item) => (
              <Tab.Pane key={item.name} eventKey={item.name.toLowerCase()}>
                <div className="tab-pane-content">
                  {item.content}
                </div>
              </Tab.Pane>
            ))}
          </Tab.Content>
        </div>
      </Tab.Container>

      {/* Modern Tab Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-lead-tabs {
          --tab-primary-color: #0d6efd;
          --tab-border-color: #e9ecef;
          --tab-bg-color: #ffffff;
          --tab-hover-bg: #f8f9fa;
          --tab-active-bg: #ffffff;
          --tab-text-color: #6c757d;
          --tab-active-text: #212529;
          --tab-border-radius: 12px;
          --tab-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .modern-tab-wrapper {
          background: var(--tab-bg-color);
          border: 1px solid var(--tab-border-color);
          border-radius: var(--tab-border-radius);
          padding: 4px;
          margin-bottom: 2rem;
          box-shadow: var(--tab-shadow);
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .modern-tab-wrapper::-webkit-scrollbar {
          display: none;
        }

        .modern-nav-tabs {
          border: none;
          gap: 2px;
          flex-wrap: nowrap;
          min-width: max-content;
        }

        .modern-nav-item {
          flex-shrink: 0;
        }

        .modern-nav-link {
          border: none !important;
          background: transparent !important;
          color: var(--tab-text-color) !important;
          padding: 12px 18px !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
          font-weight: 500 !important;
          font-size: 0.9rem !important;
          white-space: nowrap !important;
          position: relative !important;
          min-height: 48px !important;
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 10px !important;
        }

        .modern-nav-link:hover {
          background: var(--tab-hover-bg) !important;
          color: var(--tab-active-text) !important;
          transform: translateY(-1px);
        }

        .modern-nav-link.active {
          background: var(--tab-primary-color) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(13, 110, 253, 0.25) !important;
        }

        .modern-nav-link.active .tab-icon-wrapper {
          background: rgba(255, 255, 255, 0.2);
        }

        .tab-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 6px;
          transition: all 0.2s ease;
          background: rgba(13, 110, 253, 0.08);
        }

        .tab-icon {
          font-size: 0.9rem !important;
          transition: all 0.2s ease;
        }

        .tab-label {
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s ease;
          margin: 0;
        }

        .modern-tab-content {
          position: relative;
          background: var(--tab-bg-color, #ffffff);
          padding: 1.5rem;
          border-radius: var(--tab-border-radius, 12px);
          border: 1px solid var(--tab-border-color, #e9ecef);
          box-shadow: var(--tab-shadow, 0 2px 4px rgba(0,0,0,0.05));
        }

        .tab-pane-content {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .modern-tab-wrapper {
            margin: 0 -1rem 2rem -1rem;
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .modern-nav-link {
            padding: 10px 12px !important;
            font-size: 0.85rem !important;
          }

          .tab-label {
            font-size: 0.85rem;
          }
        }
        
        @media (max-width: 500px) {
          .modern-tab-content {
            padding: 1rem;
            height: auto;
            min-height: 200px;
            max-height: none;
            overflow: hidden;
            scrollbar-width: none;  /* Firefox */
            -ms-overflow-style: none;  /* IE and Edge */
          }
          
          .modern-tab-content::-webkit-scrollbar {
            display: none;  /* Chrome, Safari, Opera */
          }
          
          .tab-pane-content {
            height: auto;
          }
        }

        .modern-nav-link:focus {
          outline: 2px solid var(--tab-primary-color);
          outline-offset: 2px;
        }

        `
      }} />
    </div>
  );
};

export default LeadDetailsTab;
